import type { ComponentProps } from 'react'

import MobileDropdownBottomSheet from './MobileDropdownBottomSheet'
import { pickDataAttributes } from './pickDataAttributes'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'

type DropdownSheetProps = ComponentProps<typeof DropdownSheet>

interface Props extends DropdownSheetProps {
  /**
   * Title for the mobile bottom sheet only. Use it when the desktop dropdown
   * has no header but the sheet reads better with one. Setting it also replaces
   * the desktop `headerExtension` on mobile, so the sheet header is whatever
   * this title says.
   */
  mobileTitle?: ComponentProps<typeof MobileDropdownBottomSheet>['title']
  /**
   * Subtitle for the mobile bottom sheet only. Requires `mobileTitle`.
   */
  mobileSubtitle?: ComponentProps<typeof MobileDropdownBottomSheet>['subtitle']
  /**
   * Keep the floating dropdown on mobile.
   */
  disableMobileSheet?: boolean
}

/**
 * Drop-in replacement for `DropdownSheet` that renders a native feeling bottom
 * sheet on mobile and the floating dropdown from tablet up.
 */
function ResponsiveDropdownSheet(props: Props) {
  const { mobileTitle, mobileSubtitle, disableMobileSheet, ...dropdownProps } = props
  const isMobileScreen = useScreenSizeOnly('mobile')

  if (isMobileScreen && !disableMobileSheet) {
    return <MobileDropdownBottomSheet
      {...pickDataAttributes(dropdownProps)}
      open={dropdownProps.open}
      title={mobileTitle ?? dropdownProps.title}
      subtitle={mobileSubtitle ?? dropdownProps.subtitle}
      headerExtension={mobileTitle ? undefined : dropdownProps.headerExtension}
      onBackClick={dropdownProps.onBackClick}
      contentPadding={dropdownProps.noPaddingOnBody ? 'none' : 'default'}
      footerContent={dropdownProps.footerStart}
      footerActions={dropdownProps.footerActions}
      primaryActionProps={dropdownProps.primaryActionProps}
      isPrimaryActionHidden={dropdownProps.isPrimaryActionHidden}
      secondaryActionProps={dropdownProps.secondaryActionProps}
      isSecondaryActionHidden={dropdownProps.isSecondaryActionHidden}
      onClose={dropdownProps.onClose}
    >
      {dropdownProps.children}
    </MobileDropdownBottomSheet>
  }

  return <DropdownSheet {...dropdownProps} />
}

export default ResponsiveDropdownSheet
